<template>
  <div class="page-wrapper">

    <!-- 🔵 Хедер всегда закреплён сверху -->
    <DefaultHeader 
      title="Создание вакансии"
      subtitle="Конструктор из шагов по созданию вакансии"
      back class="fixed-header" 
      :offset="660" />

    <!-- 🩶 Серая зона с контентом -->
    <div class="page-bg">

      <!-- Шаг над контейнером -->
      <div class="step-title-wrapper">
        <div class="step-heading">
          <span class="step-eyebrow">
            <span class="step-eyebrow-dot"></span>
            Создание вакансии
          </span>

          <h2 class="step-title">
            <span>Шаг {{ currentStep }}</span>

            <span class="step-title-divider">
              из
            </span>

            <span class="step-title-total">
              {{ totalSteps }}
            </span>
          </h2>
        </div>

        <div class="step-percentage">
          <span class="percentage-value">
            {{ progressPercent }}%
          </span>

          <span class="percentage-label">
            заполнено
          </span>
        </div>
      </div>
      
      <!-- Белый контейнер -->
      <div class="step-container">

        <!-- Прогресс бар -->
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: progressWidth }"></div>
        </div>

        <!-- Кнопки -->
        <div class="step-buttons">
          <!-- Отмена -->
          <button
            v-if="currentStep === 1"
            type="button"
            class="btn cancel"
            @click="openDeleteModal"
          >
            <span class="btn-icon-box">
              <svg
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M6 6 14 14M14 6 6 14"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
              </svg>
            </span>

            <span class="btn-label">
              Отмена
            </span>
          </button>

          <!-- Назад -->
          <button
            v-else
            type="button"
            class="btn back"
            @click="prevStep"
          >
            <span class="btn-icon-box">
              <img
                src="/black-back-button.png"
                class="btn-icon"
                alt=""
              />
            </span>

            <span class="btn-label">
              Назад
            </span>
          </button>

          <!-- Далее / Готово -->
          <button
            type="button"
            class="btn next"
            :class="{
              finish: isLastStep
            }"
            :disabled="!formValid || submitting"
            @click="handleNext"
          >
            <span class="btn-label">
              {{ nextButtonTitle }}
            </span>

            <span class="btn-icon-box">
              <svg
                v-if="isLastStep"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="m5.5 10.2 2.8 2.8 6.2-6.2"
                  stroke="currentColor"
                  stroke-width="1.9"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              <img
                v-else
                src="/black-back-button.png"
                class="btn-icon rotate"
                alt=""
              />
            </span>

            <span class="btn-shine"></span>
          </button>
        </div>

      <div v-if="submitting" class="loading-overlay">
        <div class="loader"></div>
        <p class="loading-title">Создаём вакансию...</p>
      </div>

        <!-- Контент шага (единственная скроллируемая область) -->
        <div class="step-content">
          <component
            :is="currentStepComponent"
            ref="currentStepRef"
            @valid="formValid = true"
            @notValid="formValid = false"
            @complete-step="currentStep++"
            @ready="formValid = true"
          />
        </div>

      </div>
    </div>

    <Footer />
  </div>

    <div
      v-if="snackbarVisible"
      class="mobile-snackbar"
      :class="snackbarType"
    >
      {{ snackbarMessage }}
  </div>

  <ConfirmModal
    :visible="showDeleteModal"
    title="Вы действительно хотите удалить вакансию?"
    @confirm="confirmDeleteVacancy"
    @close="showDeleteModal = false"
  />

</template>

<script setup>

import { ref, computed } from 'vue';
import { useRouter, useRoute } from "vue-router";
import DefaultHeader from '../components/CreateVacancyHeader.vue';
import Footer from '../components/MiniFooter.vue';
import { v4 as uuidv4 } from 'uuid';
import MyVacanciesService from '../api/MyVacanciesService';

import StepTitle from '../components/create-vacancy/StepTitle.vue';
import StepDescription from '../components/create-vacancy/StepDescription.vue';
import StepSalary from '../components/create-vacancy/StepSalary.vue';
import StepSpecific from '../components/create-vacancy/StepSpecific.vue';
import StepHashtags from '../components/create-vacancy/StepHashtags.vue';
import StepAddress from '../components/create-vacancy/StepAddress.vue';
import StepImage from '../components/create-vacancy/StepImage.vue';
import ConfirmModal from '../components/create-vacancy/ConfirmDeleteModal.vue';
import StepCompanySelect from "../components/create-vacancy/StepCompanySelect.vue";

const snackbarVisible = ref(false);
const snackbarMessage = ref("");
const snackbarType = ref("error");
let snackbarTimer = null;

const showSnackbar = (message, type = "error") => {
  snackbarMessage.value = message;
  snackbarType.value = type;
  snackbarVisible.value = true;
  if (snackbarTimer) clearTimeout(snackbarTimer);
  snackbarTimer = setTimeout(() => {
    snackbarVisible.value = false;
  }, 2500);
};
const route = useRoute();
const router = useRouter();
const formValid = ref(false);
const showDeleteModal = ref(false);

const openDeleteModal = () => (showDeleteModal.value = true);
const isLastStep = computed(() => currentStep.value === totalSteps);
const nextButtonTitle = computed(() => isLastStep.value ? "Готово" : "Далее");

const confirmDeleteVacancy = () => {
  showDeleteModal.value = false;
  router.push('/feed');
};

const isMultipleCompanies = computed(() => {
  return route.query.isMultipleCompanies === "true";
});

let steps = [
  StepTitle,
  StepDescription,
  StepSalary,
  StepSpecific,
  StepHashtags,
  StepAddress,
  StepImage
];

if (isMultipleCompanies.value) {
  steps = [
    StepCompanySelect, 
    ...steps
  ];
}

const currentStep = ref(1);
const totalSteps = steps.length;
const currentStepComponent = computed(() => steps[currentStep.value - 1]);
const currentStepRef = ref(null);
const showModal = ref(false);
const submitting = ref(false);

const openConfirmModal = () => {
  showModal.value = true;
};

const confirmCancel = () => {
  const keysToRemove = [
    "vacancy_title",
    "vacancy_description",
    "vacancy_salary",
    "vacancy_specific",
    "vacancy_hashtags",
    "vacancy_address",
    "vacancy_is_manual",
    "vacancy_image_name",
    "vacancy_image_url",
    "vacancy_id", 
    "current_vacancy_id" 
  ];

  keysToRemove.forEach(key => localStorage.removeItem(key));

  // Закрываем модалку и уходим назад
  showModal.value = false;
  window.history.back();
};

const closeModal = () => {
  showModal.value = false;
};

const nextStep = () => {
  const stepKey = currentStep.value;

  if (isMultipleCompanies.value && stepKey === 1) {
    currentStep.value++;
    return;
  }

  if (stepKey === 1 && !localStorage.getItem("current_vacancy_id")) {
    const vacancyId = uuidv4();
    localStorage.setItem("current_vacancy_id", vacancyId);
  }

  const stepEvents = {
    1: "save-title",
    2: "save-description",
    3: "save-salary",
    4: "save-specific",
    5: "save-hashtags",
    6: "save-address",
    7: "save-image"
  };

  const adjustedStepKey = isMultipleCompanies.value ? stepKey - 1 : stepKey;

  const eventToDispatch = stepEvents[adjustedStepKey];

  if (eventToDispatch) {
    document.dispatchEvent(new CustomEvent(eventToDispatch));
  } else if (currentStep.value < totalSteps) {
    currentStep.value++;
  } else {
    handleSubmit();
  }
};

const handleNext = () => {
  if (isLastStep.value) {
    handleSubmit();
  } else {
    nextStep();
  }
};

const prevStep = () => {
  if (currentStep.value > 1) currentStep.value--;
};

const handleCancel = () => {
  window.history.back();
};

const specificRaw = JSON.parse(localStorage.getItem("vacancy_specific") || "{}");


const handleSubmit = async () => {
  submitting.value = true;
  console.log('🚀 Вакансия готова к отправке!');

  const payload = {

    vacancy_id: localStorage.getItem("current_vacancy_id"),
    title: localStorage.getItem("vacancy_title"),
    info: localStorage.getItem("vacancy_description"),
    salary: localStorage.getItem("vacancy_salary"),
    specific: {
      id: localStorage.getItem("vacancy_specific_id"),
      name: localStorage.getItem("vacancy_specific_name"),
      eng_name: localStorage.getItem("vacancy_specific_eng"),
    },
    hashtags: JSON.parse(localStorage.getItem("vacancy_hashtags") || "[]"),

    // 🖼 Изображения
    image_name: localStorage.getItem("vacancy_image_name"),
    image_url: localStorage.getItem("vacancy_image_url"),
    preview_image_base64: localStorage.getItem("vacancy_preview_image_base64"),
    image_base64: localStorage.getItem("vacancy_preview_image_base64"),

    // 📍 Адрес
    address: localStorage.getItem("vacancy_address"),
    street: localStorage.getItem("vacancy_address"), // дублируем
    address_id: localStorage.getItem("vacancy_address"),
    city: localStorage.getItem("vacancy_city"),
    city_id: localStorage.getItem("vacancy_city_id"),
    latitude: parseFloat(localStorage.getItem("vacancy_latitude")),
    longitude: parseFloat(localStorage.getItem("vacancy_longitude")),
    is_manual: localStorage.getItem("vacancy_is_manual") === "true",

    // 🔧 Прочее
    is_remote: false,
    views: 0,
    rating: 0,
    company_id: localStorage.getItem("vacancy_company_id"),
    experience: parseInt(localStorage.getItem("vacancy_experience") || "0")
  };

  console.log("📦 Payload для отправки:", payload);

  try {
    await MyVacanciesService.createVacancy(payload);
    console.log("✅ Вакансия успешно отправлена!");

    // Очистка localStorage (по желанию)
    const keysToRemove = [
      "vacancy_title",
      "vacancy_description",
      "vacancy_salary",
      "vacancy_specific",
      "vacancy_hashtags",
      "vacancy_address",
      "vacancy_is_manual",
      "vacancy_image_name",
      "vacancy_image_url",
      "vacancy_id", 
      "current_vacancy_id" 
    ];

    keysToRemove.forEach(key => localStorage.removeItem(key));
    window.location.href = "/my-vacancies"; 

  } catch (e) {
    showSnackbar("Не удалось создать вакансию. Попробуйте ещё раз.");
  } finally {
    submitting.value = false;
  }
};

const progressWidth = computed(() => {
  return `${(currentStep.value / totalSteps) * 100}%`;
});

</script>
<style scoped>

:root,
html,
body {
  margin: 0;
  padding: 0;
  overflow: hidden;
  max-width: 100vw;
  max-height: 100vh;
}

.page-wrapper {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-bg {
  flex: 1;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 30px;
  overflow: hidden;
}

.step-title-wrapper {
  width: 1100px;
  margin-bottom: 12px;
}

.step-title {
  font-size: 20px;
  font-family: "Eastman-Bold";
  color: var(--text-primary);
  margin: 0;
}

.step-container {
  width: 1100px;
  height: calc(100% - 160px);
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.progress-bar {
  margin-top: 0;
  margin-left: 0;
  width: 1100px;
  height: 14px;
  background: #eaeaea;
  overflow: hidden;
}

.step-content {
  flex: 1;
  overflow-y: auto;
  margin-top: -20px;
  padding-right: 8px;
}

/* Кнопки */

.step-buttons {
  position: relative;
  z-index: 5;

  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 11px;

  margin-top: 20px;
  margin-right: 20px;
}

.btn {
  position: relative;

  min-width: 122px;
  height: 44px;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;

  padding: 0 13px;
  overflow: hidden;

  color: #ffffff;
  border: none;
  border-radius: 14px;
  outline: none;

  cursor: pointer;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;

  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    opacity 0.22s ease,
    filter 0.22s ease;
}

.btn::before {
  content: "";

  position: absolute;
  inset: 0;
  z-index: 0;

  pointer-events: none;

  background:
    linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.11),
      transparent 55%
    );
}

.btn:hover:not(:disabled) {
  transform: translateY(-2px);
}

.btn:active:not(:disabled) {
  transform: translateY(0) scale(0.97);
}

.btn:focus-visible {
  outline: 3px solid rgba(50, 133, 245, 0.2);
  outline-offset: 3px;
}

.btn-label {
  position: relative;
  z-index: 2;

  display: inline-flex;
  align-items: center;
}

.btn-icon-box {
  position: relative;
  z-index: 2;

  width: 28px;
  height: 28px;

  display: flex;
  align-items: center;
  justify-content: center;

  flex-shrink: 0;

  color: #ffffff;
  background: rgba(255, 255, 255, 0.13);

  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 9px;

  transition:
    background-color 0.22s ease,
    transform 0.22s ease;
}

.btn:hover:not(:disabled) .btn-icon-box {
  background: rgba(255, 255, 255, 0.19);
}

.btn-icon-box svg {
  width: 16px;
  height: 16px;
}

/*
  Поддерживаем и новый btn-icon,
  и старый класс icon.
*/
.btn-icon,
.icon {
  width: 13px;
  height: 13px;

  display: block;
  object-fit: contain;

  filter: brightness(0) invert(1);
}

.btn-icon.rotate,
.icon.rotate,
.rotate {
  transform: rotate(180deg);
}

.icon-white {
  filter: brightness(0) invert(1);
}

/* Отмена */

.btn.cancel {
  background:
    linear-gradient(
      135deg,
      #df6265 0%,
      #cb454b 58%,
      #b93842 100%
    );

  box-shadow:
    0 7px 17px rgba(190, 53, 63, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.14);
}

.btn.cancel:hover:not(:disabled) {
  box-shadow:
    0 10px 23px rgba(190, 53, 63, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.17);
}

/* Назад */

.btn.back {
  background:
    linear-gradient(
      135deg,
      #737b87 0%,
      #59616d 58%,
      #484f5a 100%
    );

  box-shadow:
    0 7px 17px rgba(54, 62, 75, 0.17),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
}

.btn.back:hover:not(:disabled) {
  box-shadow:
    0 10px 23px rgba(54, 62, 75, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.btn.back:hover:not(:disabled) .btn-icon-box {
  transform: translateX(-2px);
}

/* Далее */

.btn.next {
  min-width: 132px;

  background:
    linear-gradient(
      135deg,
      #349af8 0%,
      #287ce9 52%,
      #365fda 100%
    );

  box-shadow:
    0 8px 19px rgba(39, 112, 224, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.19);
}

.btn.next:hover:not(:disabled) {
  box-shadow:
    0 11px 25px rgba(39, 112, 224, 0.28),
    inset 0 1px 0 rgba(255, 255, 255, 0.22);
}

.btn.next:hover:not(:disabled) .btn-icon-box {
  transform: translateX(2px);
}

/* Последний шаг */

.btn.next.finish {
  background:
    linear-gradient(
      135deg,
      #36b977 0%,
      #279b63 55%,
      #218653 100%
    );

  box-shadow:
    0 8px 19px rgba(37, 151, 94, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

.btn.next.finish:hover:not(:disabled) {
  box-shadow:
    0 11px 25px rgba(37, 151, 94, 0.27),
    inset 0 1px 0 rgba(255, 255, 255, 0.21);
}

.btn.next.finish:hover:not(:disabled) .btn-icon-box {
  transform: scale(1.06);
}

/* Блик */

.btn-shine {
  position: absolute;
  top: -60%;
  left: -55%;
  z-index: 1;

  width: 32%;
  height: 220%;

  pointer-events: none;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );

  transform: rotate(20deg);

  transition: left 0.65s ease;
}

.btn.next:hover:not(:disabled) .btn-shine {
  left: 130%;
}

/* Неактивная кнопка */

.btn.next:disabled {
  color: rgba(255, 255, 255, 0.76);

  background:
    linear-gradient(
      135deg,
      #a7b8d2,
      #93a8c7
    );

  box-shadow: none;

  cursor: not-allowed;
  opacity: 0.58;
  transform: none;
}

.btn.next:disabled .btn-icon-box {
  background: rgba(255, 255, 255, 0.11);
  transform: none;
}

.btn.next:disabled .btn-shine {
  display: none;
}

/* Тёмная тема */

html[data-theme="dark"] .page-bg {
  background: #0D0D0D;
}

html[data-theme="dark"] .step-container {
  background: #1A1A1A;
  box-shadow: none;
  border: 1px solid #2A2A2A;
}

html[data-theme="dark"] .step-title {
  color: #FFFFFF;
}

html[data-theme="dark"] .progress-bar {
  background: #2A2A2A;
}

html[data-theme="dark"] .progress-fill {
  background: #3B82F6;
}

html[data-theme="dark"] .step-content {
  color: #E0E0E0;
}

html[data-theme="dark"] .btn.cancel {
  background:
    linear-gradient(
      135deg,
      #b94c51,
      #96363c
    );

  box-shadow:
    0 8px 19px rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"] .btn.back {
  background:
    linear-gradient(
      135deg,
      #555b65,
      #41464f
    );

  box-shadow:
    0 8px 19px rgba(0, 0, 0, 0.2);
}

html[data-theme="dark"] .btn.next {
  background:
    linear-gradient(
      135deg,
      #327fdc,
      #315dca
    );

  box-shadow:
    0 8px 20px rgba(0, 0, 0, 0.22);
}

html[data-theme="dark"] .btn.next.finish {
  background:
    linear-gradient(
      135deg,
      #2e9e68,
      #237d52
    );
}

html[data-theme="dark"] .btn.next:disabled {
  color: rgba(255, 255, 255, 0.46);

  background:
    linear-gradient(
      135deg,
      #3f4651,
      #353b45
    );

  box-shadow: none;
  opacity: 0.7;
}

html[data-theme="dark"] .icon-white {
  filter: brightness(0) invert(1);
}

/* Прогресс */

.progress-fill {
  background: #2887F7;
  height: 100%;
  transition: width 0.3s ease;
}

/* Loading overlay */

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(8px);
  background: rgba(0, 0, 0, 0.25);
  z-index: 999;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* ⚪ Универсальный премиальный лоадер */

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.25);
  border-top-color: white;
  animation: spinner 0.8s linear infinite;
}

@keyframes spinner {
  to {
    transform: rotate(360deg);
  }
}

/* 📝 Текст поверх */

.loading-title {
  margin-top: 18px;
  font-size: 16px;
  font-family: "Eastman-Medium";
  color: white;
  opacity: 0.9;
}

/* 🌑 Для темной темы — чуть мягче */

html[data-theme="dark"] .loading-overlay {
  background: rgba(0, 0, 0, 0.35);
}

html[data-theme="dark"] .loader {
  border: 4px solid rgba(255, 255, 255, 0.15);
  border-top-color: white;
}

html[data-theme="dark"] .loading-title {
  color: #f5f5f5;
}

/* Mobile */

@media (max-width: 430px) {
  :root,
  html,
  body {
    overflow-x: hidden;
    overflow-y: auto;
    max-width: 100%;
    max-height: none;
  }

  .page-wrapper {
    width: 100%;
    min-height: 100dvh;
    height: 100dvh;
    overflow: hidden;
    background: var(--background-color);
  }

  .page-bg {
    flex: 1;
    width: 100%;
    padding: 22px 14px 90px;
    align-items: stretch;
    overflow: hidden;
  }

  .step-title-wrapper {
    width: 100%;
    margin-bottom: 10px;
  }

  .step-title {
    font-size: 16px;
    line-height: 1.2;
  }

  .step-container {
    width: 90%;
    height: auto;
    flex: 1;
    min-height: 0;
    border-radius: 5px;
    overflow: hidden;
  }

  .progress-bar {
    width: 100%;
    height: 8px;
  }

  .step-buttons {
    order: 2;

    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;

    margin: 0;
    padding: 12px;

    border-top: 1px solid rgba(0, 0, 0, 0.06);
    background: inherit;
  }

  html[data-theme="dark"] .step-buttons {
    border-top-color: rgba(255, 255, 255, 0.06);
  }

  .btn {
    width: 100%;
    min-width: 0;
    height: 44px;

    padding: 0 10px;

    font-size: 12px;
    border-radius: 13px;
  }

  .btn.next {
    min-width: 0;
  }

  .btn-icon-box {
    width: 27px;
    height: 27px;
    border-radius: 8px;
  }

  .btn-icon,
  .icon {
    width: 12px;
    height: 12px;
  }

  .step-content {
    order: 1;

    flex: 1;
    min-height: 0;

    margin-top: 0;
    padding: 16px 14px 20px;

    overflow-y: auto;
    overflow-x: hidden;

    -webkit-overflow-scrolling: touch;
  }

  .loading-overlay {
    position: fixed;
    z-index: 9999;
  }

  .loader {
    width: 42px;
    height: 42px;
  }

  .loading-title {
    font-size: 15px;
    text-align: center;
  }
}

/* Snackbar */

.mobile-snackbar {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);

  width: calc(100% - 32px);
  max-width: 360px;

  padding: 14px 16px;
  border-radius: 18px;

  background: rgba(20, 20, 20, 0.92);
  color: white;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 13px;
  line-height: 1.35;
  text-align: center;

  z-index: 10000;

  backdrop-filter: blur(16px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);

  animation: snackbarIn 0.25s ease;
}

.mobile-snackbar.error {
  background: rgba(217, 83, 79, 0.95);
}

.mobile-snackbar.success {
  background: rgba(34, 119, 230, 0.95);
}

@keyframes snackbarIn {
  from {
    opacity: 0;
    transform:
      translateX(-50%)
      translateY(12px);
  }

  to {
    opacity: 1;
    transform:
      translateX(-50%)
      translateY(0);
  }
}

@media (max-width: 430px) {
  .mobile-snackbar {
    bottom:
      calc(
        env(safe-area-inset-bottom) + 18px
      );

    width: calc(100% - 28px);
    font-size: 13px;
    border-radius: 16px;
  }
}

/* Reduced motion */

@media (prefers-reduced-motion: reduce) {
  .btn,
  .btn-icon-box,
  .btn-shine {
    transition: none;
  }

  .btn-shine {
    display: none;
  }
}

/* Progress bar */

.progress-bar {
  position: relative;

  width: 100%;
  height: 12px;

  padding: 3px;
  overflow: hidden;
  box-sizing: border-box;

  background:
    linear-gradient(
      180deg,
      #edf1f7 0%,
      #e5eaf2 100%
    );

  border-bottom: 1px solid
    rgba(30, 65, 110, 0.06);

  box-shadow:
    inset 0 1px 3px rgba(31, 55, 90, 0.07);
}

/* Заполненная часть */

.progress-fill {
  position: relative;

  height: 100%;
  min-width: 8px;

  overflow: hidden;

  background:
    linear-gradient(
      90deg,
      #2588f8 0%,
      #3b82f6 48%,
      #596ff2 100%
    );

  border-radius: 999px;

  box-shadow:
    0 1px 5px rgba(38, 119, 231, 0.3),
    0 0 10px rgba(47, 126, 239, 0.13);

  transition:
    width 0.65s
    cubic-bezier(0.22, 1, 0.36, 1);
}

/* Мягкий свет внутри заполнения */

.progress-fill::before {
  content: "";

  position: absolute;
  inset: 0;

  pointer-events: none;

  background:
    linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.3),
      rgba(255, 255, 255, 0) 65%
    );

  border-radius: inherit;
}

/* Движущийся блик */

.progress-fill::after {
  content: "";

  position: absolute;
  top: -100%;
  left: -50%;

  width: 28%;
  height: 300%;

  pointer-events: none;

  background:
    linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.5),
      transparent
    );

  transform: rotate(18deg);

  animation:
    progress-shine 2.8s ease-in-out infinite;
}

@keyframes progress-shine {
  0% {
    left: -50%;
    opacity: 0;
  }

  15% {
    opacity: 1;
  }

  55% {
    left: 125%;
    opacity: 1;
  }

  70%,
  100% {
    left: 125%;
    opacity: 0;
  }
}

/* Тёмная тема */

html[data-theme="dark"] .progress-bar {
  background:
    linear-gradient(
      180deg,
      #24262b 0%,
      #202227 100%
    );

  border-bottom-color:
    rgba(255, 255, 255, 0.05);

  box-shadow:
    inset 0 1px 4px rgba(0, 0, 0, 0.28);
}

html[data-theme="dark"] .progress-fill {
  background:
    linear-gradient(
      90deg,
      #328be9 0%,
      #4588f4 48%,
      #6675f4 100%
    );

  box-shadow:
    0 1px 7px rgba(54, 135, 241, 0.36),
    0 0 13px rgba(54, 135, 241, 0.14);
}

/* Mobile */

@media (max-width: 430px) {
  .progress-bar {
    width: 100%;
    height: 10px;
    padding: 2px;
  }

  .progress-fill {
    min-width: 6px;
  }
}

/* Отключаем анимацию при системном запрете */

@media (prefers-reduced-motion: reduce) {
  .progress-fill {
    transition: width 0.2s ease;
  }

  .progress-fill::after {
    display: none;
    animation: none;
  }
}

/* Заголовок текущего шага */

.step-title-wrapper {
  width: 1100px;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;

  margin-bottom: 14px;
}

.step-heading {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
}

.step-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 7px;

  color: #8992a3;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;
  line-height: 1;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.step-eyebrow-dot {
  width: 7px;
  height: 7px;

  flex-shrink: 0;

  background:
    linear-gradient(
      135deg,
      #32a0ff,
      #536df0
    );

  border-radius: 50%;

  box-shadow:
    0 0 0 4px rgba(40, 135, 247, 0.1);
}

.step-title {
  display: flex;
  align-items: baseline;
  gap: 8px;

  margin: 0;

  color: #202631;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 22px;
  line-height: 1.2;
}

.step-title-divider {
  color: #a5acb8;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 14px;
}

.step-title-total {
  color: #8e96a5;

  font-family: "Eastman-Medium", sans-serif;
  font-size: 16px;
}

/* Процент справа */

.step-percentage {
  min-width: 90px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  padding: 9px 13px;
  box-sizing: border-box;

  background:
    linear-gradient(
      135deg,
      rgba(40, 135, 247, 0.1),
      rgba(79, 105, 236, 0.07)
    );

  border: 1px solid rgba(40, 135, 247, 0.11);
  border-radius: 13px;

  box-shadow:
    0 5px 14px rgba(37, 107, 199, 0.06);
}

.percentage-value {
  color: #287fe9;

  font-family: "Eastman-Bold", sans-serif;
  font-size: 13px;
}

.percentage-label {
  color: #8790a0;

  font-family: "Eastman-Regular", sans-serif;
  font-size: 10px;
}

/* Тёмная тема */

html[data-theme="dark"] .step-eyebrow {
  color: #777f8d;
}

html[data-theme="dark"] .step-title {
  color: #f3f4f7;
}

html[data-theme="dark"] .step-title-divider {
  color: #676e7a;
}

html[data-theme="dark"] .step-title-total {
  color: #9299a7;
}

html[data-theme="dark"] .step-percentage {
  background:
    linear-gradient(
      135deg,
      rgba(50, 132, 231, 0.13),
      rgba(83, 104, 232, 0.09)
    );

  border-color:
    rgba(79, 147, 236, 0.14);

  box-shadow:
    0 6px 16px rgba(0, 0, 0, 0.12);
}

html[data-theme="dark"] .percentage-value {
  color: #68a8fa;
}

html[data-theme="dark"] .percentage-label {
  color: #858d9b;
}

/* Mobile */

@media (max-width: 430px) {
  .step-title-wrapper {
    width: 100%;

    align-items: flex-end;
    gap: 12px;

    margin-bottom: 12px;
  }

  .step-heading {
    gap: 6px;
  }

  .step-eyebrow {
    font-size: 9px;
  }

  .step-title {
    gap: 6px;
    font-size: 18px;
  }

  .step-title-divider {
    font-size: 12px;
  }

  .step-title-total {
    font-size: 14px;
  }

  .step-percentage {
    min-width: auto;

    padding: 8px 10px;
    border-radius: 11px;
  }

  .percentage-value {
    font-size: 12px;
  }

  .percentage-label {
    display: none;
  }
}

</style>